import { useState, useRef, useCallback, useEffect } from 'react';
import { Collapse, Loader, Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconEye, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type { AIMessage } from '~/common/vm/protocol';

export function AIScreenshotAnalysisCard({
  message,
  isLastMessage,
  isPreviewing,
  autoExpand = false,
}: {
  message: Extract<AIMessage, { type: 'screenshot-analysis' }>;
  isLastMessage: boolean;
  isPreviewing: boolean;
  autoExpand?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(autoExpand);

  const openScreenshotModal = useCallback(() => {
    modals.open({
      size: 'xl',
      centered: true,
      styles: {
        header: {
          minHeight: '40px',
          padding: '8px 16px',
        },
      },
      children: <ScreenshotModalContent base64Screenshot={message.base64Screenshot} />,
    });
  }, [message.base64Screenshot]);

  return (
    <div className="text-sm text-gray-600 dark:text-gray-400">
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        variant="subtle"
        size="xs"
        color="gray"
        justify="flex-start"
        fullWidth
        leftSection={
          isLastMessage && isPreviewing ?
            <Loader
              size={10}
              color="gray.5"
            />
          : <div className="flex items-center">
              <IconEye
                size={13}
                className="group-hover:hidden"
              />
              {isExpanded ?
                <IconChevronDown
                  size={13}
                  className="hidden group-hover:block"
                />
              : <IconChevronRight
                  size={13}
                  className="hidden group-hover:block"
                />
              }
            </div>
        }
        className="group -mb-1 -ml-1 h-auto p-0 font-normal hover:bg-transparent hover:text-gray-700 dark:hover:bg-transparent dark:hover:text-gray-200 [&>*]:gap-0.5"
      >
        {isLastMessage && isPreviewing ? 'Looking at the page...' : 'Looked at the page'}
      </Button>

      <Collapse in={isExpanded}>
        <div className="mt-2 flex w-full justify-center rounded border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800">
          <img
            src={`data:image/png;base64,${message.base64Screenshot}`}
            alt="Screenshot being analyzed"
            className="cursor-pointer rounded border border-gray-300 shadow-sm transition-opacity hover:opacity-80 dark:border-gray-600"
            style={{ maxHeight: '200px' }}
            onClick={openScreenshotModal}
          />
        </div>
      </Collapse>
    </div>
  );
}

function ScreenshotModalContent({ base64Screenshot }: { base64Screenshot: string }) {
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);

  const imgRef = useRef<HTMLImageElement>(null);
  const positionRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });

  const updateTransform = useCallback(() => {
    if (imgRef.current) {
      const { x, y } = positionRef.current;
      imgRef.current.style.transform = `scale(${scale}) translate(${x}px, ${y}px)`;
    }
  }, [scale]);

  // Update transform when scale changes
  useEffect(() => {
    updateTransform();
  }, [updateTransform]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale((prevScale) => Math.max(0.5, Math.min(3, prevScale + delta)));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - positionRef.current.x,
      y: e.clientY - positionRef.current.y,
    };
    if (imgRef.current) {
      imgRef.current.style.transition = 'none';
    }
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging) {
        positionRef.current = {
          x: e.clientX - dragStartRef.current.x,
          y: e.clientY - dragStartRef.current.y,
        };
        updateTransform();
      }
    },
    [isDragging, updateTransform],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (imgRef.current) {
      imgRef.current.style.transition = 'transform 0.2s ease-out';
    }
  }, []);

  return (
    <>
      <div
        className="flex justify-center overflow-hidden"
        onWheel={handleWheel}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <img
          ref={imgRef}
          src={`data:image/png;base64,${base64Screenshot}`}
          alt="Screenshot being analyzed - enlarged"
          className="select-none rounded border border-gray-300 shadow-md dark:border-gray-600"
          style={{
            maxHeight: '80vh',
            maxWidth: '100%',
            transform: `scale(${scale})`,
            transition: 'transform 0.2s ease-out',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          draggable={false}
          onMouseDown={handleMouseDown}
          onDragStart={(e) => e.preventDefault()}
        />
      </div>
      <div className="mt-2 text-center text-xs text-gray-500">
        Mouse wheel to zoom • Click and drag to pan (Scale: {Math.round(scale * 100)}%)
      </div>
    </>
  );
}
